import React from 'react';
import { Cookie } from 'lucide-react';
import { SEO } from '../components/SEO';

export const Cookies: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Cookie Policy"
        description="Cookie Policy for Cemates.me. Learn how we use cookies and similar technologies on our website."
        canonical="/cookies"
      />
      <div className="pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-6">
          {/* Header */}
          <div className="mb-16">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-6">
              <Cookie className="w-5 h-5 text-foreground" />
            </div>
            <h1 className="text-3xl font-semibold text-foreground mb-3">
              Cookie Policy
            </h1>
            <p className="text-muted-foreground text-sm">
              Last updated: April 27, 2024
            </p>
          </div>

          {/* Intro */}
          <div className="mb-12">
            <p className="text-muted-foreground leading-relaxed">
              This Cookie Policy explains how we use cookies and similar technologies to recognize 
              you when you visit our website.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-12">
            {/* What Are Cookies */}
            <div>
              <h2 className="text-lg font-medium text-foreground mb-4">
                What Are Cookies
              </h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Cookies are small text files that are placed on your computer by websites that you visit. 
                They are widely used to make websites work more efficiently and provide information to 
                the owners of the site.
              </p>
            </div>

            {/* How We Use Cookies */}
            <div>
              <h2 className="text-lg font-medium text-foreground mb-4">
                How We Use Cookies
              </h2>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                We use cookies for the following purposes:
              </p>
              <ul className="space-y-2">
                {[
                  'To remember your preferences and settings',
                  'To understand how you use our website',
                  'To remember your login status',
                  'To help us improve our website'
                ].map((item, index) => (
                  <li key={index} className="text-muted-foreground text-sm leading-relaxed flex gap-3">
                    <span className="text-muted-foreground/50 select-none">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Types of Cookies */}
            <div>
              <h2 className="text-lg font-medium text-foreground mb-6">
                Types of Cookies We Use
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-base font-medium text-foreground mb-2">
                    Essential Cookies
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    These cookies are necessary for the website to function properly. They enable core 
                    functionality such as security, network management, and accessibility.
                  </p>
                </div>
                <div>
                  <h3 className="text-base font-medium text-foreground mb-2">
                    Analytics Cookies
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    These cookies help us understand how visitors interact with our website by collecting 
                    and reporting information anonymously.
                  </p>
                </div>
                <div>
                  <h3 className="text-base font-medium text-foreground mb-2">
                    Preference Cookies
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    These cookies enable the website to remember choices you make (such as your language 
                    preference) and provide enhanced features.
                  </p>
                </div>
              </div>
            </div>

            {/* Managing Cookies */}
            <div>
              <h2 className="text-lg font-medium text-foreground mb-4">
                Managing Cookies
              </h2>
              <p className="text-muted-foreground text-sm leading-relaxed mb-3">
                Most web browsers allow you to control cookies through their settings preferences. 
                However, limiting cookies may affect the functionality of our website.
              </p>
              <p className="text-muted-foreground text-sm leading-relaxed">
                To learn more about cookies and how to manage them, visit{' '}
                <a 
                  href="https://www.aboutcookies.org" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-foreground underline underline-offset-2 hover:no-underline"
                >
                  aboutcookies.org
                </a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};